import { Link, useLocation } from 'react-router';

import { formatDate } from 'my-core/date-utils';
import { studentCoursePageStudySessionPath } from 'my-core/routes';
import {
  getStudySessionDateLabel,
  getStudySessionDateStatus,
  getStudySessionExpirationDate,
} from 'my-core/study-session-utils';

import StudySessionCheckoutDate from './StudySessionCheckoutDate';

import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

export default function StudySessionCheckoutConfirmation({ noDialog, onClose, studySession, titleVariant = 'h5' }) {
  const dateStatus = getStudySessionDateStatus(studySession);
  const expirationDate = getStudySessionExpirationDate(studySession);
  const { pathname } = useLocation();

  return (
    <>
      <div
        css={({ breakpoints, spacing }) => ({
          padding: spacing(2, 8),
          marginBottom: spacing(1),
          [breakpoints.down('sm')]: { padding: spacing(2) },
        })}
      >
        <Typography sx={{ mb: 3 }} variant={titleVariant}>
          Congrats!
        </Typography>
        <Typography gutterBottom>
          You've successfully registered for {studySession.course?.code} {studySession.title}. We've sent you an email
          with all the details you need to know
        </Typography>
        {studySession.disable_zoom || ['past', 'unknown'].includes(dateStatus) ?
          studySession.course && (
            <Typography gutterBottom>
              You can watch the session and download the booklet from the{' '}
              {(
                pathname.startsWith(
                  studentCoursePageStudySessionPath(studySession.course, studySession).replace(/\d+$/, ''), // ignore id part of path because may be free-weekly of weekly bundle
                )
              ) ?
                <MuiLink component="button" onClick={onClose}>
                  current page
                </MuiLink>
              : <MuiLink
                  component={Link}
                  onClick={noDialog ? undefined : onClose}
                  to={studentCoursePageStudySessionPath(studySession.course, studySession)}
                >
                  grade-booster page.
                </MuiLink>
              }
            </Typography>
          )
        : <LiveSessionDetails onClose={noDialog ? undefined : onClose} studySession={studySession} />}
        <Typography gutterBottom>
          All resources, including the recordings, are{' '}
          <strong>available until {formatDate(expirationDate, 'upcomingDate')}</strong>. You can watch the recording on
          up to 2 unique devices.
        </Typography>
      </div>
      {['live', 'upcoming'].includes(dateStatus) && (
        <div
          css={({ breakpoints, palette, spacing }) => ({
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            justifyContent: 'space-around',
            padding: spacing(4, 8),
            background: palette.background.default,
            '.SSCC-dateGroup': {
              display: 'block',
              padding: spacing(1, 0),
            },
            [breakpoints.down('sm')]: { padding: spacing(2) },
          })}
        >
          {studySession.dates.map((d, idx) => (
            <div key={d.id} className="SSCC-dateGroup">
              {studySession.dates.length > 1 && (
                <Typography variant="overline">
                  {getStudySessionDateLabel(studySession)} {idx + 1}
                </Typography>
              )}
              <StudySessionCheckoutDate date={d} studySession={studySession} />
            </div>
          ))}
        </div>
      )}
      {noDialog && (
        <Button onClick={onClose} size="large" sx={{ mt: 5, mx: 'auto', display: 'block' }} variant="contained">
          Get Started
        </Button>
      )}
    </>
  );
}

function LiveSessionDetails({ onClose, studySession }) {
  return (
    <div
      css={({ palette, shape: { borderRadiusSm }, spacing }) => ({
        margin: spacing(3, 0),
        '& > .infoBlock': {
          display: 'flex',
          alignItems: 'center',
          gap: spacing(2),
          '& + .infoBlock': { marginTop: spacing(2) },
          '& > .infoBlockStat': {
            width: 85,
            height: 85,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: palette.background.default,
            borderRadius: borderRadiusSm,
            textAlign: 'center',
          },
        },
      })}
    >
      {renderBlock(
        24,
        'Hrs before session',
        'Course Booklet Released',
        <>
          You can download the PDF booklet from the{' '}
          <MuiLink
            component={Link}
            onClick={onClose}
            to={studentCoursePageStudySessionPath(studySession.course, studySession)}
          >
            grade-booster page.
          </MuiLink>
        </>,
      )}
      {renderBlock(
        1,
        'Hr before session',
        'Zoom Link Available',
        <>
          You can join the session from the{' '}
          <MuiLink
            component={Link}
            onClick={onClose}
            to={studentCoursePageStudySessionPath(studySession.course, studySession)}
          >
            grade-booster page
          </MuiLink>{' '}
          up to 1 hour before the session start time.
        </>,
      )}
      {renderBlock(
        24,
        'Hrs after session',
        'Solutions & Recording Available',
        <>
          You can download the solution booklet and video recording from the{' '}
          <MuiLink
            component={Link}
            onClick={onClose}
            to={studentCoursePageStudySessionPath(studySession.course, studySession)}
          >
            grade-booster page.
          </MuiLink>
        </>,
      )}
    </div>
  );
  function renderBlock(stat, statText, header, description) {
    return (
      <div className="infoBlock">
        <div className="infoBlockStat">
          <Typography color="primary" variant="h6">
            {stat}
          </Typography>
          <Typography variant="body2">{statText}</Typography>
        </div>
        <div>
          <Typography variant="subtitle1">{header}</Typography>
          <Typography>{description}</Typography>
        </div>
      </div>
    );
  }
}
